import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Form from 'react-bootstrap/Form';
import ParticleService from '../ParticleService';
import { toast } from 'react-toastify';
import mapboxgl from '../mapbox';
import './styles.scss';

class FireAllControl {
  constructor(onFire) { this.onFire = onFire; }
  onAdd(map) {
    this.map = map;
    this.container = document.createElement('div');
    this.container.className = 'mapboxgl-ctrl';
    const button = document.createElement('button');
    button.className = 'map-fire-btn';
    button.textContent = 'FIRE ALL';
    button.addEventListener('click', event => {
      this.onFire();
      event.stopPropagation();
    });
    this.container.appendChild(button);
    return this.container;
  }
  onRemove() {
    this.container?.remove();
    this.map = undefined;
  }
}

const storedCenter = () => {
  const value = JSON.parse(localStorage.getItem('mapbox.lastCentre'));
  return value ? mapboxgl.LngLat.convert(value) : new mapboxgl.LngLat(-113.9749, 50.7736);
};

const storedZoom = () => JSON.parse(localStorage.getItem('mapbox.lastZoom')) || 12;

export default function Map({ session }) {
  const isAdmin = Boolean(session?.isAdmin);
  const mapContainer = useRef(null);
  const map = useRef(null);
  const markers = useRef([]);
  const locationCache = useRef(new window.Map());
  const [devices, setDevices] = useState([]);
  const [customerFilter, setCustomerFilter] = useState('');
  const [productFilter, setProductFilter] = useState('');
  const [assignmentFilter, setAssignmentFilter] = useState(isAdmin ? 'assigned' : '');
  const [onlineFilter, setOnlineFilter] = useState('');
  const [subscriptionFilter, setSubscriptionFilter] = useState('');

  const loadDevices = useCallback(async () => {
    const [result, fleetLocations] = isAdmin
      ? await Promise.all([
          ParticleService.getAdminFleetDevices(),
          ParticleService.getAdminFleetLocations(),
        ])
      : [await ParticleService.getDevices(), []];
    if (isAdmin) {
      locationCache.current.clear();
      fleetLocations.forEach(location => {
        const coordinates = location?.geometry?.type === 'Point' ? location.geometry.coordinates : null;
        if (location?.device_id && Array.isArray(coordinates) && coordinates.length >= 2 &&
            Number.isFinite(Number(coordinates[0])) && Number.isFinite(Number(coordinates[1]))) {
          locationCache.current.set(location.device_id, [Number(coordinates[0]), Number(coordinates[1])]);
        }
      });
    }
    setDevices(result.map(device => ({ ...device, adminFleet: isAdmin })));
  }, [isAdmin]);

  const fireDevice = useCallback(async device => {
    if (isAdmin && !window.confirm(`Fire ${device.name || device.id} for ${device.customerEmail || 'an unassigned device'}?`)) return;
    toast(`Firing ${device.name || device.id}`);
    const result = await ParticleService.fireCannon(device.id, isAdmin ? device.product_id : null);
    if (result.error || result.errors) toast.error(result.error || result.errors[0]);
  }, [isAdmin]);

  const fireAllCustomerDevices = useCallback(async () => {
    const customerDevices = await ParticleService.getDevices();
    customerDevices.forEach(device => ParticleService.fireCannon(device.id).then(result => {
      if (result.error) toast.error(`Could not fire ${device.name || device.id}`);
    }));
  }, []);

  useEffect(() => {
    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: 'mapbox://styles/mapbox/outdoors-v11',
      center: storedCenter(),
      zoom: storedZoom(),
    });
    map.current.addControl(new mapboxgl.GeolocateControl({
      positionOptions: { enableHighAccuracy: true },
      trackUserLocation: true,
      showUserHeading: true,
    }));
    map.current.addControl(new mapboxgl.NavigationControl({ showCompass: true }), 'top-right');
    if (!isAdmin) map.current.addControl(new FireAllControl(fireAllCustomerDevices), 'top-right');
    map.current.on('moveend', () => {
      localStorage.setItem('mapbox.lastCentre', JSON.stringify(map.current.getCenter()));
      localStorage.setItem('mapbox.lastZoom', JSON.stringify(map.current.getZoom()));
    });
    return () => {
      markers.current.forEach(marker => marker.remove());
      map.current?.remove();
      map.current = null;
    };
  }, [fireAllCustomerDevices, isAdmin]);

  useEffect(() => {
    loadDevices();
    const refresh = () => loadDevices();
    document.addEventListener('dataRefresh', refresh);
    return () => document.removeEventListener('dataRefresh', refresh);
  }, [loadDevices]);

  const filteredDevices = useMemo(() => devices.filter(device =>
    (!customerFilter || device.customerEmail === customerFilter) &&
    (!productFilter || String(device.product_id) === productFilter) &&
    (!assignmentFilter || (assignmentFilter === 'assigned' ? device.assigned : !device.assigned)) &&
    (!onlineFilter || (onlineFilter === 'online' ? device.online : !device.online)) &&
    (!subscriptionFilter || device.subscriptionStatus === subscriptionFilter)),
  [assignmentFilter, customerFilter, devices, onlineFilter, productFilter, subscriptionFilter]);

  useEffect(() => {
    let cancelled = false;
    const render = async () => {
      markers.current.forEach(marker => marker.remove());
      markers.current = [];
      const eligible = filteredDevices;
      const addMarker = async device => {
          let location = locationCache.current.get(device.id);
          if (!location) {
            // Fleet managers use Particle's bulk location endpoint. Do not fall back to
            // dozens of live variable calls when a fleet location is unavailable.
            if (isAdmin) return;
            const response = await ParticleService.getCannonLocation(
              device.id, isAdmin ? device.product_id : null);
            if (response.error || typeof response.result !== 'string') return;
            const [latitude, longitude] = response.result.split(',').map(Number);
            if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return;
            location = [longitude, latitude];
            locationCache.current.set(device.id, location);
          }
          if (cancelled || !map.current) return;
          const element = document.createElement('button');
          element.type = 'button';
          element.className = 'map-marker';
          element.title = `${device.name || device.id}${device.customerEmail ? ` — ${device.customerEmail}` : ''}`;
          element.style.backgroundImage = `url(${process.env.PUBLIC_URL + '/mapIcons/Location3.svg'})`;
          element.addEventListener('click', () => fireDevice(device));
          const marker = new mapboxgl.Marker(element).setLngLat(location).addTo(map.current);
          markers.current.push(marker);
      };
      for (let offset = 0; offset < eligible.length && !cancelled; offset += 8) {
        const batch = eligible.slice(offset, offset + 8);
        await Promise.all(batch.map(addMarker));
      }
    };
    render();
    return () => { cancelled = true; };
  }, [filteredDevices, fireDevice, isAdmin]);

  const customers = useMemo(() => [...new Set(devices.map(device => device.customerEmail).filter(Boolean))].sort(), [devices]);
  const subscriptionStatuses = useMemo(() => [...new Set(devices.map(device => device.subscriptionStatus).filter(Boolean))].sort(), [devices]);

  return (
    <div>
      {isAdmin && <div className="fleet-map-filters shadow-sm">
        <strong>Fleet map filters</strong>
        <Form.Select aria-label="Filter map by customer" value={customerFilter} onChange={event => setCustomerFilter(event.target.value)}><option value="">All customers</option>{customers.map(customer => <option key={customer} value={customer}>{customer}</option>)}</Form.Select>
        <Form.Select aria-label="Filter map by product" value={productFilter} onChange={event => setProductFilter(event.target.value)}><option value="">All products</option><option value={ParticleService.LTEProductID}>LTE</option><option value={ParticleService.WIFIProductID}>Wi-Fi</option></Form.Select>
        <Form.Select aria-label="Filter map by assignment" value={assignmentFilter} onChange={event => setAssignmentFilter(event.target.value)}><option value="">All assignments</option><option value="assigned">Assigned</option><option value="unassigned">Unassigned</option></Form.Select>
        <Form.Select aria-label="Filter map by online status" value={onlineFilter} onChange={event => setOnlineFilter(event.target.value)}><option value="">Online and offline</option><option value="online">Online</option><option value="offline">Offline</option></Form.Select>
        <Form.Select aria-label="Filter map by subscription" value={subscriptionFilter} onChange={event => setSubscriptionFilter(event.target.value)}><option value="">All subscriptions</option>{subscriptionStatuses.map(status => <option key={status} value={status}>{status}</option>)}</Form.Select>
        <small>{filteredDevices.length} matching devices. A marker appears when the device reports a valid location.</small>
      </div>}
      <div ref={mapContainer} className="map-container" />
    </div>
  );
}
